import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/useAuthStore'

// ============================================
// Constants
// ============================================

const SESSION_CHECK_INTERVAL_MS = 60000 // 1 minute
const INACTIVITY_WARNING_MS = 300000 // 5 minutes before logout warning
const ACTIVITY_EVENTS = ['click', 'mousedown', 'touchstart', 'keydown', 'scroll']

// ============================================
// Session Manager Composable
// ============================================

export function useSessionManager() {
  const router = useRouter()
  const authStore = useAuthStore()

  // State
  const showExpirationWarning = ref(false)
  const timeUntilExpiration = ref(0)
  let sessionCheckInterval: ReturnType<typeof setInterval> | null = null
  let lastActivityTime = Date.now()

  /**
   * Update last activity timestamp
   */
  function updateActivity() {
    lastActivityTime = Date.now()
    // Hide warning if user becomes active
    if (showExpirationWarning.value) {
      showExpirationWarning.value = false
      // Refresh session on activity when warning was shown
      authStore.refreshSession()
    }
  }

  /**
   * Check session status and handle expiration
   */
  function checkSession() {
    if (!authStore.isAuthenticated) {
      return
    }

    // Check if session is still valid
    const isValid = authStore.checkSession()
    if (!isValid) {
      console.log('[SessionManager] Session invalid, redirecting to login')
      router.push('/login')
      return
    }

    // Check time remaining
    const remaining = authStore.sessionTimeRemaining
    timeUntilExpiration.value = remaining

    // Show warning if less than 5 minutes remaining
    if (remaining > 0 && remaining < INACTIVITY_WARNING_MS) {
      showExpirationWarning.value = true
    }

    // Auto-refresh if session needs it and user was recently active
    if (authStore.sessionNeedsRefresh) {
      const timeSinceActivity = Date.now() - lastActivityTime
      // Only refresh if user was active in the last 5 minutes
      if (timeSinceActivity < INACTIVITY_WARNING_MS) {
        console.log('[SessionManager] Auto-refreshing session')
        authStore.refreshSession()
      }
    }
  }

  /**
   * Re-check session immediately when the app returns to foreground.
   * Mobile PWAs freeze setInterval in the background, so we cannot rely on
   * the periodic poll alone — the user may resume after the session expired.
   */
  function onVisibilityChange() {
    if (document.visibilityState !== 'visible') return
    if (!authStore.isAuthenticated) return
    checkSession()
    // Sync expiresAt with backend (handles the case where backend dropped
    // the session while local state still believes it's valid).
    authStore.refreshSession()
  }

  /**
   * Start session monitoring
   */
  function startSessionMonitoring() {
    // Initial check
    checkSession()

    // Set up interval
    sessionCheckInterval = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS)

    // Set up activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    // Re-check on tab/app focus return
    document.addEventListener('visibilitychange', onVisibilityChange)

    console.log('[SessionManager] Monitoring started')
  }

  /**
   * Stop session monitoring
   */
  function stopSessionMonitoring() {
    if (sessionCheckInterval) {
      clearInterval(sessionCheckInterval)
      sessionCheckInterval = null
    }

    // Remove activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, updateActivity)
    })

    document.removeEventListener('visibilitychange', onVisibilityChange)

    console.log('[SessionManager] Monitoring stopped')
  }

  /**
   * Extend session manually (e.g., when user clicks "Stay logged in")
   */
  async function extendSession() {
    await authStore.refreshSession()
    showExpirationWarning.value = false
    updateActivity()
  }

  /**
   * Logout and redirect
   */
  function logout() {
    stopSessionMonitoring()
    authStore.logout()
    router.push('/login')
  }

  // Lifecycle
  onMounted(() => {
    if (authStore.isAuthenticated) {
      startSessionMonitoring()
    }
  })

  onUnmounted(() => {
    stopSessionMonitoring()
  })

  return {
    // State
    showExpirationWarning,
    timeUntilExpiration,

    // Methods
    startSessionMonitoring,
    stopSessionMonitoring,
    checkSession,
    extendSession,
    logout,
    updateActivity
  }
}

export default useSessionManager
